import {
	Button,
	CircularProgress,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	Typography,
	Checkbox,
	FormControlLabel,
	useTheme,
} from '@mui/material';
import { getTeacherData, updateTeacherData } from '../../api/content-api';
import { useContext, useEffect, useState } from 'react';
import AuthContext from '../../store/auth-context';
import SnackbarContext from '../../store/snackbar-context';
import { debugError } from '../../utils/debug';
import { useNavigate } from 'react-router-dom';
import { loginPage } from '../../store/fixed-routing';
import ManagedFormContext from '../../ui/Form/MangedForm';
import { TextFormField } from './NewSchoolDialog';
import ManagedFormSelectField from '../../ui/Form/ManagedFormSelectField';
import Teacher from '../../model/Teacher';
import { useCustomTheme } from '../../store/theme-context';
import DeclaredStudentsEditor from '../Forms/Register/DeclaredStudentsEditor';
import {
	DeclaredStudentRow,
	DECLARED_STUDENTS_MAX_CHARS,
	parseStoredToRows,
	serializeDeclaredStudents,
	validateDeclaredStudents,
} from '../../utils/declared-students';

type Props = {
	teacherWpId: number | null;
	open: boolean;
	onClose: () => void;
	onSuccess?: () => void;
};
const TeacherEditDialog = ({ open, teacherWpId, onClose, onSuccess }: Props) => {
	const { palette } = useTheme();
	const { isHighContrast } = useCustomTheme();
	const { token, logout, role, isAdmin } = useContext(AuthContext);
	const navigate = useNavigate();
	const { setMsg } = useContext(SnackbarContext);
	const { reset, validate, getFormData } = useContext(ManagedFormContext);
	const [sendingRequest, setSendingRequest] = useState(false);
	const [teacher, setTeacher] = useState<Teacher>();
	const [emailVerified, setEmailVerified] = useState(true);
	const [declaredStudents, setDeclaredStudents] = useState<DeclaredStudentRow[]>([]);
	const [structuredStudentsEditor, setStructuredStudentsEditor] = useState(true);

	useEffect(() => {
		getTeacherData(token, teacherWpId)
			.then((response) => {
				if (response.status === 200) {
					const teacher = Teacher.fromApiResponse(response.data);
					setTeacher(teacher);
					setEmailVerified(teacher.emailVerified !== false);
					const parsed = parseStoredToRows(teacher.students || '');
					setDeclaredStudents(parsed.rows);
					setStructuredStudentsEditor(parsed.isStructured);
				}
			})
			.catch((exception) => {
				console.error(exception);
				setMsg({ msg: 'Błąd serwera. Spróbuj za chwilę' });
			});
	}, [teacherWpId]);

	const handleApprove = () => {
		const validationResult = validate();
		if (validationResult) {
			setMsg({ msg: validationResult, severity: 'warning' });
			return;
		}
		setSendingRequest(true);
		if (sendingRequest) {
			return;
		}
		const formData = getFormData();
		if (structuredStudentsEditor) {
			const declaredErr = validateDeclaredStudents(declaredStudents);
			if (declaredErr) {
				setMsg({ msg: declaredErr, severity: 'warning' });
				return;
			}
			const serialized = serializeDeclaredStudents(declaredStudents);
			if (serialized.length >= DECLARED_STUDENTS_MAX_CHARS) {
				setMsg({
					msg: `Przekroczono ilość znaków w polu uczniowie (maks. ${DECLARED_STUDENTS_MAX_CHARS} znaków)`,
					severity: 'warning',
				});
				return;
			}
		} else if (formData.students.length >= DECLARED_STUDENTS_MAX_CHARS) {
			setMsg({
				msg: `Przekroczono ilość znaków w polu uczniowie (maks. ${DECLARED_STUDENTS_MAX_CHARS} znaków)`,
				severity: 'warning',
			});
			return;
		}
		const request = {
			id: teacher?.id,
			...formData,
			...(structuredStudentsEditor ? { declaredStudents, students: serializeDeclaredStudents(declaredStudents) } : {}),
			...(isAdmin ? { emailVerified } : {}),
		};
		updateTeacherData(token, request)
			.then((response) => {
				if (response.status === 200) {
					setMsg({ msg: 'Dane zostały zaktualizowane', severity: 'success' });
					if (onSuccess) {
						onSuccess();
					} else {
						setTimeout(() => window.location.reload(), 2000);
					}
				} else {
					debugError('TeacherEditDialog response error:', response);
					setMsg({ msg: response.data });
				}
				setSendingRequest(false);
			})
			.catch((exception) => {
				console.error(exception);
				if (exception.response.status === 401 || exception.response.status === 403) {
					logout();
					navigate(loginPage);
					setMsg({ msg: 'Nie masz uprawnień albo Twoja sesja wygasła' });
				} else if (exception?.response?.data?.message) {
					setMsg({ msg: exception?.response?.data?.message });
				}
				setSendingRequest(false);
			});
	};

	/* 
	const districtHandler = (value: string) => {
		const district = districts.find((district: any) => district.name === value);

		if (value.length < 1) {
			setFormValidation({
				...formValidation,
				districtError: formErrors.districtError,
			});
		} else {
			setFormValidation({
				...formValidation,
				districtError: '',
			});
		}
		setFormInfo((prevState) => {
			return {
				...prevState,
				districtId: district.id,
			};
		});
		setHelperInputInfo((prevState) => {
			return {
				...prevState,
				district: value,
			};
		});
	};
	*/

	return (
		<>
			<Dialog
				open={open && !!teacher}
				onClose={() => {
					reset();
					onClose();
				}}
			>
				<DialogTitle sx={{ fontSize: '24px' }}>Dane konta</DialogTitle>
				<DialogContent>
					{isAdmin && (
						<FormControlLabel
							sx={{ mb: 1, display: 'flex' }}
							control={
								<Checkbox
									checked={emailVerified}
									onChange={(e) => setEmailVerified(e.target.checked)}
								/>
							}
							label='Adres email potwierdzony (konto aktywne — może się logować)'
						/>
					)}
					<Grid
						item
						container
						justifyContent={'space-between'}
					>
						<ManagedFormSelectField
							initialCorrect
							label={'Tytuł'}
							accessor={'personalTitle'}
							initialValue={teacher?.personalTitle}
							options={[
								{ label: 'Pan', value: 'Pan' },
								{ label: 'Pani', value: 'Pani' },
							]}
							formControlProps={{
								sx: {
									my: '10px',
									width: `calc(50% - 10px)`,
								},
							}}
							menuItemProps={{
								sx: { fontSize: '16px' },
							}}
						/>
						<TextFormField
							label={'Tytuł naukowy'}
							initialCorrect
							accessor={'academicDegree'}
							initialValue={teacher?.academicDegree}
						/>
						<TextFormField
							label={'Imię'}
							initialCorrect
							accessor={'firstName'}
							initialValue={teacher?.firstName}
						/>
						<TextFormField
							label={'Drugie imię'}
							initialCorrect
							accessor={'secondName'}
							initialValue={teacher?.secondName}
						/>
						<TextFormField
							label={'Nazwisko'}
							initialCorrect
							accessor={'lastName'}
							initialValue={teacher?.lastName}
						/>
						<TextFormField
							label={'Kategoria'}
							initialCorrect
							//disabled={role === 'teacher'}
							accessor={'category'}
							initialValue={teacher?.category}
						/>
						<TextFormField
							type={'phone'}
							initialCorrect
							optional={true}
							label={'Telefon'}
							accessor={'phone'}
							initialValue={teacher?.phone ? teacher?.phone : ' '}
						/>
						<TextFormField
							type={'email'}
							initialCorrect
							label={'Email'}
							accessor={'email'}
							initialValue={teacher?.email}
						/>
						<TextFormField
							initialCorrect
							multiline
							rows={3}
							max={255}
							fullWidth
							label={'Afiliacja'}
							accessor={'affiliation'}
							initialValue={teacher?.affiliation}
						/>
						{structuredStudentsEditor ? (
							<Grid
								item
								xs={12}
								sx={{ width: '100%', my: 1 }}
							>
								<Typography sx={{ mb: 1 }}>Uczniowie</Typography>
								<DeclaredStudentsEditor
									rows={declaredStudents}
									onChange={setDeclaredStudents}
								/>
							</Grid>
						) : (
							<TextFormField
								initialCorrect
								multiline
								rows={3}
								fullWidth
								label={'Uczniowie (podgląd — format legacy)'}
								accessor={'students'}
								initialValue={teacher?.students}
							/>
						)}
					</Grid>
					<Typography>Adres</Typography>
					<Grid
						item
						container
						justifyContent={'space-between'}
					>
						<TextFormField
							initialCorrect
							label={'Ulica'}
							accessor={'street'}
							fullWidth
							initialValue={teacher?.street}
						/>
						<TextFormField
							initialCorrect
							label={'Numer budynku'}
							accessor={'building'}
							initialValue={teacher?.building}
						/>
						<TextFormField
							initialCorrect
							optional
							label={'Numer mieszkania'}
							accessor={'apartment'}
							initialValue={teacher?.apartment}
						/>
						<TextFormField
							initialCorrect
							label={'Kod pocztowy'}
							type={'zipCode'}
							accessor={'zipCode'}
							initialValue={teacher?.zipCode}
						/>
						<TextFormField
							initialCorrect
							label={'Miasto'}
							accessor={'city'}
							initialValue={teacher?.city}
						/>
					</Grid>
				</DialogContent>
				<DialogActions>
					<Button
						sx={{
							color: isHighContrast ? palette.primary.light : palette.secondary.main,
						}}
						onClick={() => {
							onClose();
							setTimeout(() => reset(), 500);
						}}
					>
						Anuluj
					</Button>
					<Button
						sx={{
							color: isHighContrast ? palette.primary.light : palette.success.main,
						}}
						onClick={() => handleApprove()}
					>
						{sendingRequest ? <CircularProgress /> : 'Zapisz'}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export default TeacherEditDialog;
